import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Honda / Acura NSX (NA1, 1991) — the clean, low, wide mid-engine supercar at
// true scale (1 unit = 1 metre): length ~4.43, width 1.81, height 1.17
// (very LOW), wheelbase 2.53 (front axle +1.15, rear -1.38), ~0.32 m wheels.
// Nose is +Z. Authored for the `cars` showcase batch.
//
// The silhouette is ONE continuous LOW WIDE wedge — smoother and cleaner than
// an F40, with NO big wing: a low pointed nose carrying the NA1's signature
// POP-UP headlamps shown raised on the front wings -> a hard-raked windscreen
// -> a low, cab-forward black-glass cockpit bubble set well forward -> a long,
// low rear engine deck falling in one unbroken line to a blunt Kamm tail. The
// body reads a low, wide letterbox from the front (height ~0.5 of the width);
// the shoulder is ONE clean line from nose to tail with no domes or humps over
// the wheels. Big flank intake scoops ahead of the rear wheels feed the mid
// engine, and the rear carries the NSX's iconic FULL-WIDTH solid red tail-lamp
// bar on a black panel over a low diffuser and twin exhausts. Body panels
// carry the "paint" recolour slot (iconic Formula Red); `drive` spins the
// wheels and `hazards` blinks four indicators.

const PAINT = "#c9102a"; // Formula Red — the "paint" slot default
const TRIM = "#141518"; // gloss black (roof, pillars, pop-up housings, louvres)
const CLAD = "#202225"; // lower valances, sills, splitter, diffuser
const WELL = "#141518"; // wheel-well discs
const GLASS = "#161b22"; // near-black canopy / rear glazing (the "black glass" look)
const SCREEN = "#22303e"; // windscreen (a shade lighter than the tint)
const CABIN = "#0e0f12"; // interior block behind the glass
const MESH = "#0d0e10"; // engine grille + intake mesh (near-black)
const SPOKE = "#c4c9cf"; // bright multi-spoke alloys
const DISH = "#101114"; // dark wheel dish behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // pop-up headlamp glass
const AMBER = "#e8a13a";
const TAIL_RED = "#cc111d"; // the iconic full-width tail bar
const TAIL_DARK = "#3a0e12"; // small recessed centre of the tail bar
const CHROME = "#ccd1d6";
const EXHAUST = "#2b2e33"; // exhaust tip metal (dark gunmetal)
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). The car is authored VERY LOW and WIDE: the
// shoulder line sits at ~0.58 and the little cab-forward bubble roof peaks at
// only ~0.90, while the body runs a full 1.82 wide over a wide track, so every
// square view reads as a low, wide letterbox — clearly wider than tall — even
// through the perspective preview camera (which exaggerates height).
const WHEEL_R = 0.32;
const AXLE_F = 1.15;
const AXLE_R = -1.38; // wheelbase 2.53
const WHEEL_X_F = 0.83; // half track, front — tyres proud at the corners
const WHEEL_X_R = 0.86; // half track, rear — wider still, wheels planted
const TYRE_W_F = 0.22;
const TYRE_W_R = 0.3;

const SCREEN_RAKE = 1.0; // hard-raked, low NSX windscreen (top toward the tail)
const HOOD_SLOPE = 0.06; // bonnet lid diving gently to the low pointed nose

// The unbroken descending line from the low bubble roof to the Kamm tail: three
// tilted boxes whose top faces meet and overlap so the deck falls in ONE low
// line with NO flat patch and NO step. Angles fall off toward the tail.
const SAIL_ANGLE = 0.3; // rear-window backlight dropping onto the engine deck
const DECK1_ANGLE = 0.16; // engine cover falling toward the tail
const DECK2_ANGLE = 0.119; // tail deck, the last shallow fall

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One multi-spoke alloy over a low-profile tyre: tyre, brake disc, dark dish,
 *  five bright spokes proud of the tyre face, chrome centre cap. Every part
 *  shares `name` so the `drive` track spins them as one node about the axle.
 *  `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — stays proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 18, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.13, 0.13, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.2, 0.2, 0.06, 18, DISH, [side * (outer - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.05, 0.36, 0.085, SPOKE, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.028, 12, CHROME, [side * (outer - 0.002), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a body-coloured half-torus brow over the tyre. The [0, pi/2, 0] yaw
 *  puts the 0..pi arc exactly over the top half. */
function arch(side: 1 | -1, z: number, discX: number, discR: number, browR: number, browTube: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 18, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, browTube, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (discX + 0.015), WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair flanks the low grille slot; rear pair sits low at the outer
// corners, clear of the solid red tail bar so it reads unbroken.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.68, 0.36, 2.09], size: [0.14, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.68, 0.36, 2.09], size: [0.14, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.74, 0.4, -2.12], size: [0.12, 0.06, 0.04] },
  { name: "indicator-rr", pos: [0.74, 0.4, -2.12], size: [0.12, 0.06, 0.04] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear (wide track — wheels planted at the corners) --------------
  P.push(
    ...wheel(-1, WHEEL_X_F, TYRE_W_F, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, TYRE_W_F, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, TYRE_W_R, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, TYRE_W_R, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.55, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.9, 0.37, 0.34, 0.045)); // front arch, low lip tucked under the shoulder
    P.push(...arch(s, AXLE_R, 0.93, 0.39, 0.36, 0.05)); // flared rear arch, clearly wider
  }
  P.push(box(1.74, 0.12, 3.5, TRIM, [0, 0.2, -0.05])); // underbody pan

  // -- Body masses (paint) — a low, WIDE tub; ONE continuous shoulder at ~0.66,
  //    lifted just above the wheel tops so the wheels tuck UNDER the arches and
  //    the shoulder stays a single unbroken line (no domes over the wheels). --
  P.push(paint(1.82, 0.44, 3.5, [0, 0.44, -0.05])); // main tub, sill to shoulder, full length

  // Low pointed wedge nose: a low front prow face, a wide bonnet lid rising to
  // the shoulder as one full-width crown (no separate dome fenders), and a
  // lower nose bridging down to the splitter so there is no under-gap.
  P.push(paint(1.56, 0.26, 0.16, [0, 0.45, 2.06])); // low front prow face
  P.push(paint(1.72, 0.06, 1.4, [0, 0.63, 1.4], [HOOD_SLOPE, 0, 0])); // wide bonnet lid, top on the shoulder
  P.push(paint(1.72, 0.24, 0.5, [0, 0.33, 1.86])); // lower nose bridging tub to prow (no under-gap)
  P.push(box(1.7, 0.07, 0.16, CLAD, [0, 0.2, 2.07])); // low front splitter
  P.push(box(1.62, 0.14, 0.3, CLAD, [0, 0.24, 1.97])); // front valance filling back to the arch

  // -- Rear haunches: a subtle FLAT flare, its top on the shoulder line -------
  // Flat quarter panels (no domes) so the shoulder stays one clean low line.
  for (const s of S) P.push(paint(0.14, 0.44, 1.7, [s * 0.87, 0.44, -1.05])); // haunch flare, outer edge ~0.94

  // -- Engine bay block under the sloping cover (never an open deck) ----------
  P.push(box(1.5, 0.32, 1.6, MESH, [0, 0.52, -1.05])); // dark engine bay mass under the cover

  // -- The unbroken low deck behind the cabin: backlight sail -> engine cover
  //    -> tail deck, each tilted box overlapping the next so the bubble falls
  //    to the tail in ONE low line with NO flat patch, NO tall wall.
  P.push(paint(1.42, 0.32, 0.78, [0, 0.62, -0.5], [-SAIL_ANGLE, 0, 0])); // backlight sail, tucked under the roof
  P.push(paint(1.64, 0.3, 0.72, [0, 0.57, -1.17], [-DECK1_ANGLE, 0, 0])); // engine cover deck
  P.push(paint(1.68, 0.26, 0.6, [0, 0.53, -1.74], [-DECK2_ANGLE, 0, 0])); // tail deck, lands low

  // Rear glass lying on the sail; a black louvre band on the cover slope.
  P.push(box(0.96, 0.03, 0.66, GLASS, [0, 0.83, -0.5], [-SAIL_ANGLE, 0, 0])); // rear window
  P.push(box(0.9, 0.03, 0.44, MESH, [0, 0.76, -1.05], [-DECK1_ANGLE, 0, 0])); // engine mesh panel
  for (let i = 0; i < 5; i++) {
    const z = -0.98 - 0.1 * i; // from -0.98 back to -1.38
    const y = 0.735 + 0.161 * (z + 1.17); // ride the falling cover slope
    P.push(box(0.96, 0.02, 0.045, TRIM, [0, y, z], [-DECK1_ANGLE, 0, 0]));
  }
  // Subtle body-colour lip spoiler tucked onto the tail edge — NOT a wing.
  P.push(paint(1.5, 0.05, 0.14, [0, 0.64, -1.9]));

  // -- Greenhouse (a LOW, cab-forward black-glass bubble; hard-raked screen) ---
  P.push(box(1.14, 0.46, 0.05, SCREEN, [0, 0.79, 0.6], [-SCREEN_RAKE, 0, 0])); // hard-raked windscreen
  for (const s of S) P.push(box(0.06, 0.44, 0.08, TRIM, [s * 0.5, 0.79, 0.6], [-SCREEN_RAKE, 0, 0])); // thin A-pillars
  P.push(box(1.04, 0.05, 0.7, TRIM, [0, 0.9, 0.06])); // black roof, LOW; edges overlap screen + sail
  P.push(box(1.0, 0.28, 0.92, CABIN, [0, 0.76, 0.1])); // dark interior block sealing the bubble
  for (const s of S) {
    P.push(box(0.03, 0.22, 0.68, GLASS, [s * 0.5, 0.79, 0.06], [0, 0, s * 0.05])); // side glass, small tumblehome
    P.push(box(0.05, 0.22, 0.07, TRIM, [s * 0.5, 0.79, -0.26], [0, 0, s * 0.05])); // C-pillar closing the bubble
  }

  // -- Flank intake scoops on the haunches (ahead of the rear wheels) --------
  for (const s of S) {
    P.push(box(0.05, 0.24, 0.46, MESH, [s * 0.92, 0.45, -0.72])); // big side intake mouth
    P.push(paint(0.06, 0.05, 0.52, [s * 0.9, 0.58, -0.72])); // scoop upper lip
    P.push(paint(0.06, 0.05, 0.52, [s * 0.9, 0.33, -0.72])); // scoop lower lip
    P.push(paint(0.06, 0.26, 0.06, [s * 0.9, 0.45, -0.49])); // scoop front wall
    P.push(box(0.03, 0.1, 2.7, CLAD, [s * 0.9, 0.26, -0.05])); // rocker sill (reaches both arches)
  }

  // -- Front fascia: POP-UP headlamps shown RAISED as clean rectangular pods --
  // Each pod is a black rectangular housing standing clearly proud of the wing
  // (its base overlapping the bonnet so no gap opens) with a light lens on its
  // forward face — the NA1 "lights up" look, readable from every view.
  for (const s of S) {
    P.push(box(0.36, 0.18, 0.2, TRIM, [s * 0.56, 0.66, 1.72])); // raised pop-up housing, base sunk into the wing
    P.push(box(0.34, 0.12, 0.06, LAMP, [s * 0.56, 0.7, 1.83])); // rectangular lens, facing forward
    P.push(box(0.34, 0.03, 0.06, "#8fa5b2", [s * 0.56, 0.62, 1.83])); // lower reflector strip
  }
  P.push(box(0.9, 0.1, 0.05, MESH, [0, 0.36, 2.06])); // low central grille slot, no big grille
  for (let i = -2; i <= 2; i++) {
    P.push(box(0.02, 0.08, 0.02, "#26282c", [i * 0.17, 0.36, 2.08])); // grille slats
  }
  for (const s of S) P.push(box(0.12, 0.07, 0.04, LAMP, [s * 0.52, 0.3, 2.06])); // small fog lamps
  P.push(cyl(0.045, 0.045, 0.02, 16, CHROME, [0, 0.5, 2.12], [Math.PI / 2, 0, 0])); // nose badge disc
  P.push(box(0.4, 0.09, 0.02, PLATE, [0, 0.54, 2.09])); // small low front plate

  // -- Rear fascia: the iconic FULL-WIDTH SOLID red tail bar on a black panel --
  P.push(box(1.74, 0.44, 0.08, TRIM, [0, 0.52, -2.06])); // black rear panel
  P.push(box(1.68, 0.18, 0.07, TAIL_RED, [0, 0.62, -2.11])); // full-width SOLID red tail bar
  P.push(box(0.3, 0.12, 0.075, TAIL_DARK, [0, 0.62, -2.13])); // small recessed centre section
  P.push(cyl(0.05, 0.05, 0.02, 16, CHROME, [0, 0.62, -2.16], [Math.PI / 2, 0, 0])); // centre badge disc
  // Central black vent below the bar, flanked by twin exhausts.
  P.push(box(0.5, 0.14, 0.03, MESH, [0, 0.38, -2.09]));
  for (const dx of [-0.13, 0.13]) {
    P.push(cyl(0.06, 0.06, 0.09, 14, EXHAUST, [dx, 0.3, -2.11], [Math.PI / 2, 0, 0]));
  }
  P.push(box(1.5, 0.16, 0.1, CLAD, [0, 0.24, -2.06])); // rear diffuser valance
  for (const s of S) for (let i = 0; i < 3; i++) {
    P.push(box(0.03, 0.12, 0.02, "#0a0b0d", [s * (0.28 + i * 0.13), 0.24, -2.12])); // diffuser fins
  }
  P.push(box(0.4, 0.09, 0.02, PLATE, [-0.42, 0.42, -2.12])); // small rear plate, offset to one side

  // -- Furniture: small door mirrors and door handles -------------------------
  for (const s of S) {
    P.push(box(0.12, 0.03, 0.04, TRIM, [s * 0.68, 0.64, 0.45])); // mirror stalk overlapping the body
    P.push(paint(0.06, 0.08, 0.09, [s * 0.77, 0.66, 0.45])); // mirror head
    P.push(box(0.012, 0.06, 0.07, GLASS, [s * 0.805, 0.66, 0.46])); // mirror glass, facing back
    P.push(box(0.02, 0.025, 0.11, CHROME, [s * 0.885, 0.55, -0.05])); // door handle
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const hondaNsx = defineModel({
  id: "honda-nsx",
  name: "Honda NSX",
  batchId: "cars",
  tags: ["car", "honda", "acura", "nsx", "supercar", "sports", "jdm", "classic"],
  summary: "A 1991 Honda NSX mid-engine supercar at true scale, in Formula Red.",
  description:
    "A 1991 Honda / Acura NSX (NA1) at real 1 unit = 1 metre scale (about 4.43 " +
    "long, 1.81 wide, a very low 1.17 tall, 2.53 wheelbase). The silhouette is " +
    "one continuous low, wide mid-engine wedge, smoother and cleaner than an " +
    "F40 and with no big wing: a low pointed nose carrying the NA1's signature " +
    "pop-up headlamps shown raised on the front wings, a hard-raked windscreen " +
    "over a low, cab-forward black-glass cockpit bubble, and a long low rear " +
    "engine deck falling in one unbroken line to a blunt Kamm tail. The car " +
    "reads as a low, wide letterbox from the front, with one clean shoulder " +
    "line from nose to tail (no domes over the wheels), a wide track, and big " +
    "flank intake scoops ahead of the rear wheels feeding the mid engine. The " +
    "rear carries the iconic full-width solid red tail-lamp bar on a black " +
    "panel over a low diffuser and twin exhausts, on bright multi-spoke alloys " +
    "with low-profile tyres. Body panels carry the 'paint' recolour slot " +
    "(Formula Red); `drive` spins the wheels and `hazards` blinks the four " +
    "indicators.",
  author: "Fable 5",
  footprint: 2.37,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
